import { useState, type KeyboardEvent } from 'react'
import { ArrowUp } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'

interface AiCardPromptProps {
  placeholder: string
  /** Sends the prompt as a chat about whatever the card holds. */
  onSubmit: (prompt: string) => void
  className?: string
}

/**
 * The field at the foot of a grey card Highlight wrote, a meeting's brief or a
 * reminder's description, for asking about what's in it. Enter sends and
 * Shift+Enter breaks the line.
 */
export function AiCardPrompt({ placeholder, onSubmit, className }: AiCardPromptProps) {
  const [prompt, setPrompt] = useState('')
  const canSend = prompt.trim().length > 0

  const send = () => {
    if (!canSend) return
    onSubmit(prompt.trim())
    setPrompt('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    send()
  }

  return (
    <div
      className={twMerge(
        'corner-smooth bg-background-raised hover:border-hover flex shrink-0 items-end gap-2 rounded-lg border py-1 pl-2.5 pr-1 transition-colors focus-within:border-[color:var(--focus-ring-color)]',
        className,
      )}
    >
      <textarea
        aria-label={placeholder}
        placeholder={placeholder}
        value={prompt}
        rows={1}
        onChange={(event) => setPrompt(event.target.value)}
        onKeyDown={handleKeyDown}
        // Grows with the text up to four lines, then scrolls. The placeholder alone stays on one line and
        // truncates, so only what the user types makes it taller. The wrapper's border is the focus state.
        className="text-foreground type-ui-sm placeholder:text-foreground-placeholder max-h-20 min-w-0 flex-1 resize-none self-center bg-transparent py-0.5 [field-sizing:content] placeholder-shown:truncate placeholder-shown:[field-sizing:fixed] focus-visible:outline-none"
      />
      <Button size="xs" shape="square" aria-label="Send" disabled={!canSend} onClick={send}>
        <ArrowUp />
      </Button>
    </div>
  )
}
